<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ScheduledReportsAPI from 'dashboard/api/scheduledReports';

const store = useStore();

// Report options are fetched from the backend (single source of truth) so new
// reports appear here automatically. Fallback list used only if the fetch fails.
const REPORT_TYPES = ref([
  { value: 'summary', label: 'Summary metrics' },
  { value: 'conversations_list', label: 'Conversations list' },
  { value: 'csat_list', label: 'CSAT list' },
  { value: 'captain_list', label: 'Captain AI list' },
]);
const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Period presets + per-report columns are fetched from the backend (single source
// of truth). Fallbacks used only if the fetch fails.
const PERIOD_OPTIONS = ref([
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
]);
const REPORT_COLUMNS_MAP = ref({});
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const timezones = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch (_) {
    return ['UTC', 'Asia/Riyadh', 'Asia/Amman', 'Africa/Cairo', 'Europe/London'];
  }
})();

const defaultTz = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
})();

const schedules = ref([]);
const loading = ref(false);
const saving = ref(false);
const form = ref(null); // null = list view, object = editing/creating

const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = Array.from({ length: 60 }, (_, i) => i);
const daysOfMonth = Array.from({ length: 28 }, (_, i) => i + 1);

const typeLabel = v => REPORT_TYPES.value.find(t => t.value === v)?.label || v;
const periodLabel = v =>
  PERIOD_OPTIONS.value.find(p => p.value === v)?.label || v;

// Columns available for the currently selected report (empty for reports with no table)
const availableColumns = computed(
  () => REPORT_COLUMNS_MAP.value[form.value?.report_type] || []
);
const allColumnKeys = type =>
  (REPORT_COLUMNS_MAP.value[type] || []).map(c => c.key);

const fetchFormOptions = async () => {
  try {
    const { data } = await ScheduledReportsAPI.formOptions();
    if (Array.isArray(data?.report_types) && data.report_types.length) {
      REPORT_TYPES.value = data.report_types;
    }
    if (Array.isArray(data?.periods) && data.periods.length) {
      PERIOD_OPTIONS.value = data.periods;
    }
    if (data?.columns && typeof data.columns === 'object') {
      REPORT_COLUMNS_MAP.value = data.columns;
    }
  } catch (_) {
    // keep fallback lists
  }
};
const pad = n => String(n).padStart(2, '0');

const fetchAll = async () => {
  loading.value = true;
  try {
    const { data } = await ScheduledReportsAPI.get();
    schedules.value = data || [];
  } catch (_) {
    useAlert('Could not load scheduled reports.');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchAll();
  fetchFormOptions();
});

const blankForm = () => ({
  id: null,
  name: 'New Schedule',
  report_type: 'summary',
  frequency: 'daily',
  period: 'yesterday',
  custom_start: '',
  custom_end: '',
  column_keys: [],
  hour: 8,
  minute: 0,
  day_of_week: 1,
  day_of_month: 1,
  timezone: defaultTz,
  recipients: '',
  subject: '',
  description: '',
  business_hours: false,
  enabled: true,
});

const newSchedule = () => {
  form.value = blankForm();
  // start with every column selected for the default report type
  form.value.column_keys = allColumnKeys(form.value.report_type);
};

const editSchedule = s => {
  const known = allColumnKeys(s.report_type);
  const saved = Array.isArray(s.column_keys) ? s.column_keys : [];
  // keep only saved keys that still exist for this report; empty selection = all columns
  const validSaved = known.length ? saved.filter(k => known.includes(k)) : saved;
  form.value = {
    ...blankForm(),
    ...s,
    column_keys: validSaved.length ? validSaved : allColumnKeys(s.report_type),
    recipients: (s.recipients || []).join(', '),
  };
};

// When the report type changes, reset the column selection to all columns of the
// new report (the previous report's keys don't apply).
watch(
  () => form.value?.report_type,
  (newType, oldType) => {
    if (!form.value || newType === oldType || oldType === undefined) return;
    form.value.column_keys = allColumnKeys(newType);
  }
);

const cancel = () => {
  form.value = null;
};

const recipientsArray = () =>
  form.value.recipients
    .split(',')
    .map(e => e.trim())
    .filter(Boolean);

const save = async () => {
  const f = form.value;
  if (!f.name?.trim()) {
    useAlert('Please enter a schedule name.');
    return;
  }
  if (recipientsArray().length === 0) {
    useAlert('Please add at least one recipient email.');
    return;
  }
  if (f.period === 'custom' && (!f.custom_start || !f.custom_end)) {
    useAlert('Please set both custom dates.');
    return;
  }
  const payload = {
    name: f.name,
    report_type: f.report_type,
    frequency: f.frequency,
    period: f.period,
    custom_start: f.period === 'custom' ? f.custom_start : null,
    custom_end: f.period === 'custom' ? f.custom_end : null,
    // only meaningful for reports that have a table; [] = all columns
    column_keys: availableColumns.value.length ? f.column_keys : [],
    hour: f.hour,
    minute: f.minute,
    day_of_week: f.frequency === 'weekly' ? f.day_of_week : null,
    day_of_month: f.frequency === 'monthly' ? f.day_of_month : null,
    timezone: f.timezone,
    recipients: recipientsArray(),
    subject: f.subject,
    description: f.description,
    business_hours: f.business_hours,
    enabled: f.enabled,
    format: 'pdf',
  };
  saving.value = true;
  try {
    if (f.id) await ScheduledReportsAPI.update(f.id, payload);
    else await ScheduledReportsAPI.create(payload);
    useAlert('Schedule saved.');
    form.value = null;
    await fetchAll();
  } catch (e) {
    useAlert(e.response?.data?.error || 'Could not save the schedule.');
  } finally {
    saving.value = false;
  }
};

const remove = async s => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
  try {
    await ScheduledReportsAPI.delete(s.id);
    await fetchAll();
  } catch (_) {
    useAlert('Could not delete the schedule.');
  }
};

const sendNow = async s => {
  try {
    await ScheduledReportsAPI.sendNow(s.id);
    useAlert('Report sent.');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Could not send the report.');
  }
};

const scheduleTime = s => `${pad(s.hour)}:${pad(s.minute)}`;
const isAdmin = computed(() => store.getters.getCurrentRole === 'administrator');
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">Scheduled Reports</h1>
      <button
        v-if="!form"
        class="px-4 py-1.5 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10"
        @click="newSchedule"
      >
        + New Schedule
      </button>
    </div>

    <p v-if="!isAdmin" class="text-sm text-n-slate-11">
      Only administrators can manage scheduled reports.
    </p>

    <!-- LIST -->
    <template v-else-if="!form">
      <div class="overflow-x-auto rounded-xl border border-n-weak">
        <table class="w-full min-w-[960px] text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-n-slate-11">
              <th class="px-4 py-3 font-medium">Name</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Report</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Period</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Frequency</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Time</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Recipients</th>
              <th class="px-4 py-3 font-medium whitespace-nowrap">Status</th>
              <th class="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="px-4 py-8 text-center text-n-slate-10">Loading…</td>
            </tr>
            <tr v-else-if="!schedules.length">
              <td colspan="8" class="px-4 py-8 text-center text-n-slate-10">
                No scheduled reports yet. Click “New Schedule” to create one.
              </td>
            </tr>
            <tr
              v-for="s in schedules"
              v-else
              :key="s.id"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="px-4 py-3 align-top text-n-slate-12 font-medium min-w-[200px] max-w-[340px]">{{ s.name }}</td>
              <td class="px-4 py-3 align-top text-n-slate-11 whitespace-nowrap">{{ typeLabel(s.report_type) }}</td>
              <td class="px-4 py-3 align-top text-n-slate-11 whitespace-nowrap">{{ periodLabel(s.period) }}</td>
              <td class="px-4 py-3 align-top text-n-slate-11 capitalize whitespace-nowrap">{{ s.frequency }}</td>
              <td class="px-4 py-3 align-top text-n-slate-11 whitespace-nowrap">{{ scheduleTime(s) }}</td>
              <td class="px-4 py-3 align-top text-n-slate-11 truncate max-w-[220px]">
                {{ (s.recipients || []).join(', ') }}
              </td>
              <td class="px-4 py-3 align-top">
                <span
                  class="inline-block px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                  :class="s.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'"
                >{{ s.enabled ? 'Active' : 'Paused' }}</span>
              </td>
              <td class="px-4 py-3 align-top whitespace-nowrap text-right">
                <button class="text-n-blue-text hover:underline mr-3" @click="sendNow(s)">Send now</button>
                <button class="text-n-blue-text hover:underline mr-3" @click="editSchedule(s)">Edit</button>
                <button class="text-n-ruby-9 hover:underline" @click="remove(s)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- FORM -->
    <div v-else-if="isAdmin" class="max-w-3xl mx-auto rounded-xl border border-n-weak p-5 space-y-4">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Schedule name *</label>
        <input v-model="form.name" type="text" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Report</label>
          <select v-model="form.report_type" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="t in REPORT_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Send report</label>
          <select v-model="form.frequency" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="fr in FREQUENCIES" :key="fr.value" :value="fr.value">{{ fr.label }}</option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-4">
        <div v-if="form.frequency === 'weekly'">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Day of week</label>
          <select v-model.number="form.day_of_week" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="(d, i) in WEEKDAYS" :key="i" :value="i">{{ d }}</option>
          </select>
        </div>
        <div v-if="form.frequency === 'monthly'">
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Day of month</label>
          <select v-model.number="form.day_of_month" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="d in daysOfMonth" :key="d" :value="d">{{ d }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Hour</label>
          <select v-model.number="form.hour" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="h in hours" :key="h" :value="h">{{ pad(h) }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Minute</label>
          <select v-model.number="form.minute" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
            <option v-for="m in minutes" :key="m" :value="m">{{ pad(m) }}</option>
          </select>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Time zone</label>
        <select v-model="form.timezone" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
          <option v-for="tz in timezones" :key="tz" :value="tz">{{ tz }}</option>
        </select>
      </div>

      <!-- Data window (period). Resolved relative to send time in the schedule's timezone. -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Report period <span class="text-n-slate-10 font-normal">(the data window each email covers)</span></label>
        <select v-model="form.period" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12">
          <option v-for="p in PERIOD_OPTIONS" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
        <p class="mt-1 text-xs text-n-slate-10">
          Calculated fresh on every send. e.g. “Yesterday” always sends the full previous day’s data at the scheduled time.
        </p>
      </div>

      <div v-if="form.period === 'custom'" class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Custom start</label>
          <input v-model="form.custom_start" type="date" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">Custom end</label>
          <input v-model="form.custom_end" type="date" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
        </div>
      </div>

      <!-- Columns to include in the emailed table (only for reports that have a table) -->
      <div v-if="availableColumns.length">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Columns to include</label>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 p-3 rounded-lg border border-n-weak bg-n-solid-1 max-h-72 overflow-y-auto">
          <label
            v-for="col in availableColumns"
            :key="col.key"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input v-model="form.column_keys" type="checkbox" :value="col.key" class="shrink-0" />
            <span class="truncate">{{ col.label }}</span>
          </label>
        </div>
        <p class="mt-1 text-xs text-n-slate-10">Ticked columns appear in the emailed report, in this order.</p>
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Send to * <span class="text-n-slate-10 font-normal">(comma-separated emails)</span></label>
        <input v-model="form.recipients" type="text" placeholder="a@x.com, b@y.com" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Subject</label>
        <input v-model="form.subject" type="text" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">Description</label>
        <textarea v-model="form.description" rows="3" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12" />
      </div>

      <div class="flex items-center gap-4">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.business_hours" type="checkbox" /> Business hours values
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.enabled" type="checkbox" /> Active
        </label>
        <span class="text-xs text-n-slate-10">Format: PDF</span>
      </div>

      <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
        <button class="px-4 py-1.5 text-sm rounded-lg text-n-slate-11 hover:bg-n-slate-3" @click="cancel">Cancel</button>
        <button
          class="px-4 py-1.5 text-sm rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>
